<script setup>
import { useI18n } from 'vue-i18n';
import {
  getVarLabel,
  getVarPlaceholder,
} from 'dashboard/constants/fupVariables';

const props = defineProps({
  availableTypes: { type: Array, default: () => [] },
  selectedTipoId: { type: [String, null], default: null },
  selectedTipo: { type: [Object, null], default: null },
  variaveisForm: { type: Object, default: () => ({}) },
  needsVariaveis: { type: Boolean, default: false },
  actionLoading: { type: Boolean, default: false },
});

const emit = defineEmits([
  'select-tipo',
  'do-create',
  'reset-form',
  'update-variavel',
]);

const { t: $t } = useI18n();

function allVarsFilled() {
  return Object.values(props.variaveisForm).every(v => v && v.trim() !== '');
}
</script>

<template>
  <div class="fup-create">
    <div class="fup-create__header">
      <span class="fup-create__title">
        {{ $t('CONVERSATION.FOLLOW_UP.CREATE.TITLE') }}
      </span>
      <span class="fup-create__subtitle">
        {{ $t('CONVERSATION.FOLLOW_UP.CREATE.SELECT_TYPE') }}
      </span>
    </div>

    <!-- Type selector -->
    <div class="fup-create__types">
      <button
        v-for="tipo in props.availableTypes"
        :key="tipo.id"
        class="fup-type-chip"
        :class="{ 'fup-type-chip--selected': props.selectedTipoId === tipo.id }"
        :aria-label="tipo.nome"
        @click="emit('select-tipo', tipo.id)"
      >
        {{ tipo.nome }}
      </button>
    </div>

    <!-- Variables form -->
    <div
      v-if="props.selectedTipo && props.needsVariaveis"
      class="fup-create__vars"
    >
      <div class="fup-create__vars-header">
        <span class="fup-create__vars-title">
          {{ $t('CONVERSATION.FOLLOW_UP.CREATE.FORM_TITLE') }}
        </span>
        <span class="fup-create__vars-subtitle">
          {{ $t('CONVERSATION.FOLLOW_UP.CREATE.FORM_DESCRIPTION') }}
        </span>
      </div>
      <div
        v-for="(_val, key) in props.variaveisForm"
        :key="key"
        class="fup-create__field"
      >
        <label class="fup-create__label">{{ getVarLabel(key) }}</label>
        <input
          class="fup-create__input"
          :value="props.variaveisForm[key]"
          :placeholder="getVarPlaceholder(key)"
          @input="emit('update-variavel', { key, value: $event.target.value })"
        />
      </div>
      <div class="fup-create__actions">
        <button
          class="fup-btn fup-btn--primary"
          :disabled="!allVarsFilled() || props.actionLoading"
          :aria-label="
            props.actionLoading
              ? $t('CONVERSATION.FOLLOW_UP.CREATE.SUBMITTING')
              : $t('CONVERSATION.FOLLOW_UP.CREATE.SUBMIT')
          "
          @click="emit('do-create')"
        >
          {{
            props.actionLoading
              ? $t('CONVERSATION.FOLLOW_UP.CREATE.SUBMITTING')
              : $t('CONVERSATION.FOLLOW_UP.CREATE.SUBMIT')
          }}
        </button>
        <button class="fup-btn fup-btn--secondary" @click="emit('reset-form')">
          {{ $t('CONVERSATION.FOLLOW_UP.CREATE.CANCEL') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.fup-create {
  @apply p-4 rounded-lg bg-n-slate-1 dark:bg-n-solid-1 border border-n-weak;
}

.fup-create__header {
  @apply flex flex-col gap-0.5 mb-3 pb-2 border-b border-n-weak;
}

.fup-create__title {
  @apply font-semibold text-sm text-n-slate-12;
}

.fup-create__subtitle {
  @apply text-[10px] text-n-slate-11 font-normal;
}

.fup-create__types {
  @apply flex flex-wrap gap-2 mb-3;
}

.fup-create__vars {
  @apply flex flex-col gap-3 mt-3 pt-3 border-t border-n-weak;
}

.fup-create__vars-header {
  @apply flex flex-col gap-0.5 mb-2;
}

.fup-create__vars-title {
  @apply text-xs font-semibold text-n-slate-12;
}

.fup-create__vars-subtitle {
  @apply text-[10px] text-n-slate-11 font-normal;
}

.fup-create__field {
  @apply flex flex-col gap-1.5;
}

.fup-create__label {
  @apply text-xs font-semibold text-n-slate-11 capitalize flex items-center gap-1;
}

.fup-create__label::before {
  content: '•';
  @apply text-n-blue-9;
}

.fup-create__input {
  @apply w-full px-3 py-2 text-xs rounded-lg border-2 border-n-weak bg-white dark:bg-n-background
         text-n-slate-12 transition-all
         focus:outline-none focus:border-n-brand focus:ring-2 focus:ring-n-brand/20;
}

.fup-create__actions {
  @apply flex gap-2 mt-4;
}

.fup-type-chip {
  @apply px-3 py-1.5 rounded-lg text-xs font-medium border-2 border-n-weak text-n-slate-11
         hover:bg-n-slate-2 hover:border-n-slate-5 cursor-pointer transition-all duration-150;
}

.fup-type-chip--selected {
  @apply bg-gradient-to-br from-n-blue-3 to-n-blue-2 border-n-blue-7 text-n-blue-12
         shadow-sm font-semibold;
}

.fup-btn {
  @apply px-3 py-1.5 min-h-[44px] rounded-lg text-xs font-semibold cursor-pointer transition-all duration-150
         disabled:opacity-50 disabled:cursor-not-allowed shadow-sm;
}

.fup-btn--primary {
  @apply bg-gradient-to-r from-n-brand to-n-blue-10 text-white hover:shadow-md;
}

.fup-btn--secondary {
  @apply bg-n-slate-3 dark:bg-n-solid-3 text-n-slate-12 hover:bg-n-slate-4 border border-n-weak;
}
</style>
